<template>
  <div class="payment-page payment-page-confirmation">
    <ValidationObserver ref="observer" v-slot="{ handleSubmit, errors, invalid, validated }" slim>
      <form @submit.prevent="handleSubmit(processPayment)">
        <h1 id="wb-cont" class="container">
          {{ $t("payment_confirmation_title") }}
        </h1>
        <div role="alert" v-if="invalid && validated" class="alert alert-danger">
          <h4>
            <span class="container">
              <img src="../assets/circle.png" class="error-icon" alt />
              {{ $t("errorSummary", { errCount: countErrors(errors) }) }}
            </span>
          </h4>
          <ul class="container">
            <li v-for="(error, index) in realErrors(errors)" :key="index" v-html="error[0]"></li>
          </ul>
        </div>
        <div class="container">
          <div class="payment-summary clearfix">
            <p tabindex="-1">
              <span class="pull-right">
                <small>{{ $t("total") }}</small>
                <br />
                ${{ cartInfo.displayTotal }}
              </span>
              <small>{{ $t("payment_method") }}</small>
              <br />
              <span v-if="paymentInfo.paymentMethod == 'voucher'">{{ $t("voucher") }}: {{ paymentInfo.voucherCode
              }}</span>
              <span v-if="paymentInfo.paymentMethod == 'gift_certificate'">{{ $t("gift_certificate") }}:
                {{ paymentInfo.giftCertificate }}</span>
              <span v-if="getPayments.length">
                <span v-for="payment in payments" :key="payment._id">
                  {{ $t('voucher') }} - {{ payment.internalId }} -
                  {{ payment.displayCurrencyFormattedBalanceAvailableForCart }}
                  <br />
                </span>
                <small v-if="paymentInfo.paymentMethod == 'online_credit'"><br>{{ $t('balance_on') }}</small><br>
              </span>
              <span v-if="paymentInfo.cardType">{{ paymentInfo.cardType }} {{ obfuscatedCard }}</span>
            </p>
            <p v-if="paymentInfo.addressStreet.length">
              <small>{{ $t("billing_information") }}</small>
              <br />
              {{ paymentInfo.addressStreet }}, {{ paymentInfo.addressCity }},
              {{ paymentInfo.province }} {{ paymentInfo.country }}
              {{ paymentInfo.addressPostalCode }}
            </p>
          </div>
          <div class>
            <h2>{{ $t("customer_information") }}</h2>
            <div class="row payment-name">
              <div class="col-sm-6">
                <div class="form-group">
                  <label for="paymentFirstName">{{ $t("fname") }}</label>
                  <input type="text" name="paymentFirstName" id="paymentFirstName" v-model="paymentInfo.firstName"
                    class="form-control" readonly />
                </div>
              </div>
              <div class="col-sm-6">
                <div class="form-group">
                  <label for="paymentLastName">{{ $t("lname") }}</label>
                  <input type="text" name="paymentLastName" id="paymentLastName" class="form-control"
                    v-model="paymentInfo.lastName" readonly />
                </div>
              </div>
            </div>
            <div class="row customer-contact">
              <div class="col-sm-6">
                <div class="form-group">
                  <ValidationProvider :rules="{
                    required: {
                      fieldLabel: $t('phone'),
                      fieldId: '#customerPhone',
                    },
                  }" v-slot="{ errors }" slim>
                    <label for="customerPhone">{{ $t("phone") }}</label>
                    <input type="text" name="customerPhone" id="customerPhone" v-model="customerPhone"
                      class="form-control" />
                    <span class="label label-danger form-error" v-if="errors.length">
                      <strong>
                        <span class="prefix">{{ $t("errorPrefix") }}</span>
                        <span v-html="errors[0]"></span>
                      </strong>
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="col-sm-6">
                <ValidationProvider rules="email" v-slot="{ errors }" slim>
                  <div class="form-group">
                    <label for="customerEmail">{{ $t("email") }}</label>
                    <input type="text" name="customerEmail" id="customerEmail" class="form-control"
                      v-model="customerEmail" />
                    <span class="label label-danger form-error" v-if="errors.length">
                      <strong>
                        <span class="prefix">{{ $t("errorPrefix") }}</span>
                        <span v-html="errors[0]"></span>
                      </strong>
                    </span>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div class="row customer-contact">
              <div class="col-sm-6">
                <div class="form-group" v-if="customFields.length">
                  <div class="custom-field-wrapper" v-for="customField in customFields[0].fields"
                    :key="customField.internalId">
                    <template v-if="customField.type == 1">
                      <label :for="customField.internalId">{{
                          customField.text
                      }}</label>
                      <input type="text" :name="customField.internalId" :id="customField.internalId"
                        @change="customFieldChange" class="form-control" :requied="customField.required" />
                    </template>
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="form-group">
                  <div v-if="customFields.length">
                    <div class="custom-field-wrapper" v-for="customField in customFields[0].fields"
                      :key="customField.internalId">
                      <template v-if="customField.type == 2">
                        <div class="checkbox">
                          <ValidationProvider :rules="{
                            required: {
                              fieldLabel: customField.text,
                              fieldId: '#' + customField.internalId,
                            },
                          }" v-slot="{ errors, validate }" validate slim>
                            <label :for="customField.internalId">
                              <input type="checkbox" :id="customField.internalId" @change="
                                [
                                  customFieldChange($event),
                                  validate(customFieldValidation($event)),
                                ]
                              " :required="customField.required" />
                              {{ customField.text }}
                            </label>
                            <span class="label label-danger form-error" v-if="errors.length">
                              <strong>
                                <span class="prefix">{{
                                    $t("errorPrefix")
                                }}</span>
                                <span v-html="errors[0]"></span>
                              </strong>
                            </span>
                          </ValidationProvider>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div class="checkbox">
                    <ValidationProvider :rules="{
                      required: {
                        fieldLabel: 'Terms and Conditions',
                        fieldId: '#termsConsent',
                      },
                    }" v-slot="{ errors }" slim>
                      <label for="termsConsent">
                        <input type="checkbox" id="termsConsent" v-model="termsConsent" />
                        I have read and accept the
                        <a href="#termsModal" data-toggle="modal">terms and conditions</a>
                      </label>
                      <span class="label label-danger form-error" v-if="errors.length">
                        <strong>
                          <span class="prefix">{{ $t("errorPrefix") }}</span>
                          <span v-html="errors[0]"></span>
                        </strong>
                      </span>
                    </ValidationProvider>
                  </div>
                  <div class="checkbox">
                    <label for="marketingConsent">
                      <input type="checkbox" id="marketingConsent" v-model="marketingConsent" />
                      YES, I would like to receive fares and offers from Ontario
                      Northland.
                    </label>
                  </div>
                </div>
              </div>
              <div class="col-sm-12 text-center">
                <a href="#orderDetails" class="btn btn-ghost-blue" data-toggle="collapse" data-target="#orderDetails">
                  <span class="sr-only">Show</span>
                  {{ $t("order_details") }}
                </a>
              </div>
            </div>
            <div id="orderDetails" class="collapse">
              <h2>{{ $t("order_details") }}</h2>
              <cartItemsDetails />
            </div>
          </div>
        </div>
        <div class="form-group text-center mini-widget--actions">
          <a href="#" class="btn btn-ghost-yellow mrgn-rght-md" @click.prevent="modifyTrip">{{ $t("previous") }}</a>
          
          <button type="submit" id="tripNext" class="btn btn-primary">
            {{ $t("next_process_payment") }}
          </button>
          <a href="#" class="btn btn-ghost-gray" @click.prevent="emptyCart"><i class="fa fa-trash"></i> {{ $t("cancel")
          }}</a>
        </div>
      </form>
    </ValidationObserver>
    <div class="modal fade" tabindex="-1" role="dialog" id="termsModal">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 class="modal-title">Terms and Conditions</h4>
          </div>
          <div class="modal-body" v-html="currentProductTerms"></div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" data-dismiss="modal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from "../i18n";
import Vue from "vue";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required, email } from "vee-validate/dist/rules";
import { mapState, mapGetters } from "vuex";
import cartItemsDetails from "../components/cartItemsDetails";
// Override the default message.
extend("required", {
  ...required,
  params: ["fieldLabel", "fieldId"],
  message: (fieldName, placeholders) => {
    let transMessage = i18n.t("dynamicError", {
      fieldId: placeholders.fieldId,
      fieldLabel: placeholders.fieldLabel,
    });
    return transMessage;
  },
});
extend("email", {
  ...email,
  message: "Please enter a valid email",
});
export default {
  name: "PaymentConfirmationPage",
  components: {
    ValidationObserver,
    ValidationProvider,
    cartItemsDetails,
  },
  data() {
    return {
      customerPhone: "",
      customerEmail: "",
      customerAirMiles: "",
      termsConsent: false,
      marketingConsent: false,
      localCustomFields: [],
    };
  },
  watch: {},
  mounted() { },
  created() {
    this.$store.commit("GET_CUSTOM_FIELDS");
    this.customerEmail = this.$store.state.cart.items.tickets[0].passengers[0].email;
  },
  computed: {
    cartInfo() {
      return this.$store.state.cart;
    },
    obfuscatedCard() {
      let number = this.$store.state.paymentInfo.ccnumber;
      return number.substr(0, 4) + " **** **** " + number.substr(-4);
    },
    currentProductTerms() {
      let terms = this.$store.state.current_product.terms;
      terms = terms.replace(/(\n)+/g, "<br />");
      return "<p>" + terms + "</p>";
    },
    orderInfo() {
      return this.$store.state.order;
    },
    ...mapState(["paymentInfo", "payments", "customFields"]),
    ...mapGetters(["getPayments"]),
  },
  methods: {
    modifyTrip: function () {
      this.$router.back();
    },
    pushToArray: function (arr, obj) {
      const index = arr.findIndex((e) => e.internalId === obj.internalId);
      if (index === -1) {
        arr.push(obj);
      } else {
        Vue.set(arr, index, obj);
      }
    },
    customFieldChange: function (event) {
      let setValue = "";
      if (event.target.value == "on" && event.target.checked) {
        setValue = "true";
      } else if (event.target.value == "on" && event.target.checked == false) {
        setValue = "false";
      } else {
        setValue = event.target.value;
      }
      this.pushToArray(this.localCustomFields, {
        internalId: String(event.target.id),
        value: String(setValue),
      });
    },
    customFieldValidation: function (event) {
      if (event.target.required) {
        return event.target.value == "on";
      } else if (!event.target.required) {
        return false;
      } else {
        return true;
      }
    },
    realErrors(obj) {
      for (var propName in obj) {
        if (
          obj[propName] === null ||
          obj[propName] === undefined ||
          !obj[propName].length
        ) {
          delete obj[propName];
        }
      }
      return obj;
    },
    countErrors: function (errors) {
      const errorCount = Object.values(errors);
      let count = 0;
      for (let i = 0; i < errorCount.length; i++) {
        if (errorCount[i].length) {
          count++;
        }
      }
      return count;
    },
    emptyCart: function () {
      this.$store.commit("RESET_TRIP");
      this.$store.commit("EMPTY_CART");
      this.$router.push("/");
    },
    processPayment: function () {
      // Fires after validation has been done.
      this.$store.commit("setCustomerInfo", {
        customerEmail: this.customerEmail,
        customerPhone: this.customerPhone,
        customerAirMiles: this.customerAirMiles,
        marketingConsent: this.marketingConsent,
        termsConsent: this.termsConsent,
      });
      this.$store.commit("SET_CUSTOM_FIELDS", this.localCustomFields);
      this.$store
        .dispatch("createOrderShowConfirmation")
        .then((response) => {
          // If the order status is error.
          // eslint-disable-next-line no-console
          //console.log(response.status);
          if (
            response.status == "400" ||
            response.status == "401" ||
            response.status == "500"
          ) {
            alert(
              "Sorry an error occured while creating your order the details are as follows: " +
              response.message
            );
          } else if (response.status == "200") {
            // If order is paid go to payment processed page
            if (response.data.status == "paid") {
              // Trigger sending confirmation email.
              this.$store.commit("SEND_ORDER_EMAIL");
              // Clear cart and trip after sending email
              this.$store.commit("RESET_TRIP");
              this.$store.commit("EMPTY_CART");
              // Redirect to success page after
              this.$router.push("/cart/payment/processed");
            } else if (response.data.status == "error") {
              // If payment is incomplete or there is an error.
              alert(
                "Sorry there was an error processing your order, the details are as follows: " +
                response.data.processedPayments[0].status +
                ": " +
                response.data.processedPayments[0].errorCode +
                ". Please correct the information and try again."
              );
              this.$store.commit("setPaymentInfo", {});
              this.$router.push("/cart/payment");
            }
          }
        })
        .catch(({ response }) => {
          // Refactor to use Betterez API response message for errors.
          alert("Sorry an error occured here are the details: " + response.data.message);
        });
    },
  },
  filters: {
    fixedtwo: function (value) {
      var number = +value;
      return number.toFixed(2);
    },
    methodMap: function (value) {
      if (value == "voucher") {
        return "Voucher";
      } else if (value == "gift_certificate") {
        return "Gift Certificate";
      } else {
        return "Credit";
      }
    },
  },
};
</script>
<style lang="scss">
.payment-summary {
  background-color:#fff;
  color:#757070;
  border: 1px solid #898989;
  small {
    font-size: 15px;
    font-weight: 300;
  }
}

.btn-ghost-red {
  margin-left: 15px;
}
.mini-widget--actions{
  margin-top:20px;
}
</style>